import React, { useRef, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useSwipeToClose from '../../hooks/useSwipeToClose';
import { useMealPlan } from '../../contexts/MealPlanContext';
import { capDisplayFreshness } from '../../utils/freshnessDisplay';
import ImagePreviewDrawer from '../ProductList/admin/ImagePreviewDrawer';
import { RecipeInstructions, translateQty } from './RecipeContent';
import {
  getProductImage,
  getLocalizedProductName,
  findMatchedProduct,
} from '../../utils/recipeIngredientMatching';

function RecipeDrawer({ 
  selectedRecipe, 
  isOpen, 
  onClose, 
  imagesLoading,
  onDislikeRecipe,
  isGeneratingAlternatives,
  dislikedRecipe,
  numberOfPeople,
  cookingStyle,
  groceryListLoaded = true,
  hideRemoveButton = false,
  cookingState = null,
  onStartCooking = null,
  onCompleteCooking = null,
  showPromoButton = false,
  onAddToPromo = null,
  isAddingToPromo = false
}) {
  const { t } = useTranslation('mealplan');
  const { t: tRecipes } = useTranslation('recipes');
  const { containerRef, dragOffset, shouldRender, isVisible, DragHandle } = useSwipeToClose(onClose, isOpen);
  const { productList } = useMealPlan();
  const [previewImage, setPreviewImage] = useState(null);

  const handleAddToPromo = () => {
    if (onAddToPromo && selectedRecipe) {
      onAddToPromo(selectedRecipe);
    }
  };
  
  // Build ingredient-to-product mapping for current recipe
  const ingredientProducts = useMemo(() => {
    if (!selectedRecipe?.ingredients || !productList?.length) return {};
    const map = {};
    for (const ing of selectedRecipe.ingredients) {
      const key = ing.product || ing.name || '';
      map[key] = findMatchedProduct(ing, productList);
    }
    return map;
  }, [selectedRecipe?.ingredients, productList]);
  
  // Refs for scrolling to sections
  const instructionsRef = useRef(null);
  const tipsRef = useRef(null);
  const ingredientsRef = useRef(null);
  const equipmentRef = useRef(null);
  
  // Track drawer open time for auto-transition
  const drawerOpenTimeRef = useRef(null);
  const autoTransitionTimerRef = useRef(null);
  const hasAutoTransitionedRef = useRef(false);

  // Auto-transition to "Cooking in progress" after 3 minutes
  React.useEffect(() => {
    console.log('🔍 RecipeDrawer auto-transition effect:', {
      isOpen,
      hasRecipe: !!selectedRecipe,
      recipeTitle: selectedRecipe?.title,
      hasOnStartCooking: !!onStartCooking,
      cookingState,
      hasAutoTransitioned: hasAutoTransitionedRef.current
    });
    
    if (isOpen && selectedRecipe && onStartCooking) {
      const recipeId = selectedRecipe.id || selectedRecipe.title;
      
      // Only start timer if cooking hasn't started yet and we haven't auto-transitioned before
      if (!cookingState?.startedAt && !cookingState?.isCompleted && !hasAutoTransitionedRef.current) {
        drawerOpenTimeRef.current = Date.now();
        
        console.log('⏰ Starting 3-minute auto-transition timer for recipe:', recipeId);
        
        // Set timer for 3 minutes (180000 ms)
        autoTransitionTimerRef.current = setTimeout(() => {
          console.log('🕐 Auto-transitioning to cooking in progress after 3 minutes for recipe:', recipeId);
          hasAutoTransitionedRef.current = true;
          onStartCooking(recipeId, false); // Don't open drawer on auto-transition
        }, 180000); // 3 minutes
      } else {
        console.log('⏭️ Skipping auto-transition - cooking already started, completed, or already auto-transitioned');
      }
    } else if (!isOpen) {
      // Reset auto-transition flag when drawer closes
      hasAutoTransitionedRef.current = false;
    }
    
    // Cleanup on unmount or when drawer closes
    return () => {
      if (autoTransitionTimerRef.current) {
        console.log('🧹 Clearing auto-transition timer');
        clearTimeout(autoTransitionTimerRef.current);
        autoTransitionTimerRef.current = null;
      }
      drawerOpenTimeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedRecipe]); // Removed cookingState from dependencies to prevent timer reset

  // Scroll to section function
  const scrollToSection = (ref) => {
    if (ref.current) {
      ref.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
    }
  };

  if (!shouldRender) return null;

  const portal = createPortal(
    <div 
      className={`fixed flex items-end justify-center inset-0 z-[200] drawer-overlay`}
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition: 'background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
      onClick={onClose}
    >
      <div 
        ref={containerRef}
        className="flex flex-col w-full bg-[#fff] rounded-[12px_12px_0px_0px] pb-[0px] max-w-5xl mx-auto shadow-lg drawer-content overflow-hidden"
        style={{
          maxHeight: 'calc(100dvh - 44px - env(safe-area-inset-top, 0px))',
          height: 'auto',
          transform: isVisible ? `translateY(${Math.max(0, dragOffset)}px)` : 'translateY(100%)',
          transition: dragOffset === 0 ? 'transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DragHandle />
        
        {/* Header section - Fixed */}
        <div className="flex-shrink-0 pt-8 pb-0">
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-10"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z" fill="#56554D"/>
            </svg>
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 px-8">
          {selectedRecipe && (
            <>
              <div className="flex flex-col pb-32">
                {/* Image */}
                <div className="w-full mb-6">
                  {imagesLoading[selectedRecipe.title] || selectedRecipe.fetchingDetailsForImage ? (
                    <div className="rounded-[12px] w-full h-64 bg-gray-200 animate-pulse flex items-center justify-center">
                      <span className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px]">
                        {selectedRecipe.fetchingDetailsForImage ? t('loading.preparingDetails') : t('loading.generatingImage')}
                      </span>
                    </div>
                  ) : selectedRecipe.imageURL ? (
                    <img 
                      src={selectedRecipe.imageURL} 
                      alt={selectedRecipe.title} 
                      className="rounded-[12px] w-full object-cover max-h-96 md:aspect-[16/5] aspect:[16/10]"
                    />
                  ) : (
                    <div className="rounded-[12px] w-full h-64 bg-gray-100 flex items-center justify-center">
                      <span className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px]">{t('loading.imageNotAvailable')}</span>
                    </div>
                  )}
                </div>

                {/* Metadata */}
                <div className="flex flex-row flex-wrap gap-1 mb-0.5">
                  {selectedRecipe.mealType && (
                    <>
                      <span className='text-[12.4px] font-normal text-[#65635D] leading-[20px]'>{selectedRecipe.mealType}</span>
                      <span className='text-[12.4px] font-normal text-[#C1C0BE] leading-[20px]'>•</span>
                    </>
                  )}
                  {(selectedRecipe.cookingTime || selectedRecipe.cooking_time) && (
                    <>
                      <span className='text-[12.4px] font-normal text-[#65635D] leading-[20px]'>
                        {String(selectedRecipe.cookingTime || selectedRecipe.cooking_time).replace(/\s*min\s*$/i, '')} min
                      </span>
                      <span className='text-[12.4px] font-normal text-[#C1C0BE] leading-[20px]'>•</span>
                    </>
                  )}
                  {(selectedRecipe.freshness || selectedRecipe.maxStorageTime) && (
                    <span className='text-[12.4px] font-normal text-[#65635D] leading-[20px]'>
                      Fresh for {capDisplayFreshness(selectedRecipe.freshness || selectedRecipe.maxStorageTime)} days
                    </span>
                  )}
                </div>
                  
                {/* Title */}
                <h2 className="
                  text-[var(--Color-Grays-Gray-10,#0D0C07)]
                  text-[16px]
                  font-bold
                  leading-[28px]
                  tracking-[-1%]
                  w-full
                  mb-3
                ">{selectedRecipe.title}</h2>
                
                {/* Description */}
                <div className="mb-0">
                  {selectedRecipe.instructions && selectedRecipe.instructions.full_description ? (
                    <div className="mb-0">
                      <p className="text-[var(--Color-Grays-Gray-950,#353227)] text-[14px] leading-[24px]">{selectedRecipe.instructions.full_description}</p>
                    </div>
                  ) : (
                    <p className="text-[var(--Color-Grays-Gray-950,#353227)] text-[14px] leading-[24px] mb-0">{selectedRecipe.description}</p>
                  )}
                </div>

                {showPromoButton && (
                  <div className="flex justify-start pt-3">
                    <button
                      onClick={handleAddToPromo}
                      className="inline-flex justify-center items-center h-[32px] px-[12px] rounded-[8px] border-[1px] border-[#0D0C07] text-center text-[13.2px] font-medium leading-[26px] text-[#0D0C07] md:hover:bg-[#0D0C07] md:hover:text-[#fff] transition-colors"
                      disabled={isAddingToPromo}
                    >
                      {isAddingToPromo ? 'Saving...' : 'Add to Promo'}
                    </button>
                  </div>
                )}

                {/* I don't like it button - Added after Description }
                {!hideRemoveButton && (
                  <div className="flex justify-start pt-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDislikeRecipe(selectedRecipe);
                        onClose(); // Close the drawer when disliking
                      }}
                      className={`
                        inline-flex justify-center items-center
                        h-[32px] px-[12px]
                        rounded-[8px]
                        border-[1px]
                        text-center text-[13.2px] font-medium leading-[26px]
                        transition-colors
                        ${!groceryListLoaded || isGeneratingAlternatives 
                          ? 'border-[#E2E1DF] text-[#E2E1DF] cursor-not-allowed' 
                          : 'border-[#D40B0B] text-[#D40B0B] md:hover:text-[#fff] md:hover:border-[#B50B0B] md:hover:bg-[#B50B0B]'
                        }
                      `}
                      disabled={!groceryListLoaded || isGeneratingAlternatives}
                    >
                      {isGeneratingAlternatives && dislikedRecipe?.title === selectedRecipe.title ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                          {t('loading.processing')}
                        </>
                      ) : !groceryListLoaded ? (
                        <>
                          {t('loading.title')}
                        </>
                      ) : (
                        <>
                          {t('recipeCard.iDontLikeIt')}
                        </>
                      )}
                    </button>
                  </div>
                )*/}
                
                {/* Cooking Instructions */}
                <div className='mt-8 order-2' ref={instructionsRef}>
                  <div className="flex items-center justify-between mb-6 pt-8 border-t-[1px] border-[var(--Color-Grays-Gray-3,#DCDCD8)]">
                    <h3 className="inline text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[18.8px] font-bold leading-[36px border-b-[2px] border-[#009900]">{t('recipeCard.cookingInstructions')}</h3>
                  </div>
                  {selectedRecipe.loadingInstructions ? (
                    <div className="animate-pulse space-y-2 mt-5">
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    </div>
                  ) : selectedRecipe.instructions ? (
                    <RecipeInstructions instructions={selectedRecipe.instructions} />
                  ) : (
                    <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px]">{t('loading.loadingInstructions')}</p>
                  )}
                  
                  {/* Complete Cooking Button - Show if cooking is in progress */}
                  {(() => {
                    console.log('🔍 Complete Cooking Button check:', {
                      hasStartedAt: !!cookingState?.startedAt,
                      isCompleted: cookingState?.isCompleted,
                      hasOnCompleteCooking: !!onCompleteCooking,
                      cookingState,
                      cookingStateKeys: cookingState ? Object.keys(cookingState) : [],
                      startedAtValue: cookingState?.startedAt,
                      shouldShow: cookingState?.startedAt && !cookingState?.isCompleted && onCompleteCooking
                    });
                    return null;
                  })()}
                  {cookingState?.startedAt && !cookingState?.isCompleted && onCompleteCooking && (
                    <div className="flex justify-center mt-8 pt-6 border-t-[1px] border-[var(--Color-Grays-Gray-3,#DCDCD8)]">
                      <button
                        onClick={() => {
                          const recipeId = selectedRecipe.id || selectedRecipe.title;
                          console.log('✅ Complete Cooking button clicked for:', recipeId);
                          onCompleteCooking(recipeId);
                        }}
                        className="
                          flex items-center justify-center
                          w-full max-w-md h-[48px] px-6
                          bg-[#090] text-white
                          rounded-[12px]
                          border-[0.5px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                          text-[#FFF] text-center
                          text-[14px] font-semibold leading-[24px]
                          md:hover:border-[#060]
                          md:hover:bg-[#060]
                          bg-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                          shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_1px_rgba(0,102,0,0.6)]
                          transition-colors
                        "
                      >
                        <span className="inline-flex items-center justify-center">
                          
                          {t('recipeCard.completeCooking')}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Tips */}
                {selectedRecipe.instructions && selectedRecipe.instructions.tips && (
                  <div className='mt-8 order-3' ref={tipsRef}>
                    <div className="flex items-center justify-between mb-5 pt-8 border-t-[1px] border-[var(--Color-Grays-Gray-3,#DCDCD8)]">
                      <h3 className="inline text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[18.8px] font-bold leading-[36px border-b-[2px] border-[#009900]">{tRecipes('drawer.tips')}</h3>
                    </div>
                    <p className="block mt-5 text-[var(--Color-Grays-Gray-950,#353227)] text-[14.8px] leading-[26px]">{selectedRecipe.instructions.tips}</p>
                  </div>
                )}
                
                {/* Ingredients */}
                <div className='mt-8 order-1' ref={ingredientsRef}>
                  <div className="flex items-center justify-between mb-5 pt-8 border-t-[1px] border-[var(--Color-Grays-Gray-3,#DCDCD8)]">
                    <h3 className="inline text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[18.8px] font-bold leading-[36px border-b-[2px] border-[#009900]">{tRecipes('drawer.ingredients')}</h3>
                    {numberOfPeople && (() => {
                      const mealTypeKey = (selectedRecipe?.mealType || '').toLowerCase();
                      const isCookFor2Days = cookingStyle?.[mealTypeKey]?.cookFor2Days || false;
                      return (
                      <span className="inline-flex items-center
                            pl-2 pr-3 py-1
                            text-[12px] text-[#fff] font-medium
                            rounded-[10px]
                            cursor-default
                            border-[0.5px] border-[#0D0C07]
                            bg-[#0D0C07]">
                              <span className="mr-1 w-[24px]"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
<path fillRule="evenodd" clipRule="evenodd" d="M12.0002 5.375C10.1822 5.375 8.7085 6.84873 8.7085 8.66667C8.7085 10.4846 10.1822 11.9583 12.0002 11.9583C13.8181 11.9583 15.2918 10.4846 15.2918 8.66667C15.2918 6.84873 13.8181 5.375 12.0002 5.375ZM15.1147 12.6315C16.2882 11.7084 17.0418 10.2755 17.0418 8.66667C17.0418 5.88223 14.7846 3.625 12.0002 3.625C9.21573 3.625 6.9585 5.88223 6.9585 8.66667C6.9585 10.2755 7.71211 11.7084 8.88559 12.6315C8.0676 13.0024 7.31472 13.5199 6.6674 14.1672C5.25306 15.5816 4.4585 17.4998 4.4585 19.5C4.4585 19.9832 4.85025 20.375 5.3335 20.375C5.81675 20.375 6.2085 19.9832 6.2085 19.5C6.2085 17.964 6.81869 16.4908 7.90484 15.4047C8.99098 14.3185 10.4641 13.7083 12.0002 13.7083C13.5362 13.7083 15.0093 14.3185 16.0955 15.4047C17.1816 16.4908 17.7918 17.964 17.7918 19.5C17.7918 19.9832 18.1836 20.375 18.6668 20.375C19.1501 20.375 19.5418 19.9832 19.5418 19.5C19.5418 17.4998 18.7473 15.5816 17.3329 14.1672C16.6856 13.5199 15.9327 13.0024 15.1147 12.6315Z" fill="#ffffff"/>
</svg></span>
                        {numberOfPeople} {numberOfPeople === 1 ? tRecipes('drawer.person') : tRecipes('drawer.persons')}{isCookFor2Days ? ` ${tRecipes('drawer.for2Days')}` : ''}
                      </span>
                      );
                    })()}
                  </div>
                  <div className="space-y-0">
                    {selectedRecipe.ingredients?.map((ingredient, idx) => {
                      const matchedProduct = ingredientProducts[ingredient.product || ingredient.name || ''];
                      const productImage = matchedProduct ? getProductImage(matchedProduct) : '/assets/products/no-image.jpg';
                      const displayName = (matchedProduct ? getLocalizedProductName(matchedProduct) : null) || ingredient.product || ingredient.name;
                      
                      const openPreview = () => setPreviewImage({ src: productImage, name: displayName });
                      return (
                        <div key={idx} className="flex items-center gap-3 py-3 border-b border-[var(--Color-Grays-Gray-2,#F0EFED)] last:border-b-0">
                          {/* Product Image */}
                          <div
                            className="flex-shrink-0 w-12 h-12 rounded-[8px] overflow-hidden bg-gray-100 cursor-pointer active:opacity-70 transition-opacity"
                            onClick={openPreview}
                          >
                            <img 
                              src={productImage} 
                              alt={displayName}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = '/assets/products/no-image.jpg';
                              }}
                            />
                          </div>
                          
                          {/* Product Info */}
                          <div className="flex-1 min-w-0 cursor-pointer" onClick={openPreview}>
                            <h4 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-semibold leading-[20px] truncate">
                              {displayName}
                            </h4>
                          </div>
                          
                          {/* Quantity */}
                          <div className="flex-shrink-0">
                            <span className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[13px] font-medium leading-[20px] bg-[var(--Color-Grays-Gray-2,#F5F3F0)] px-2.5 py-1 rounded-[6px]">
                              {translateQty(ingredient.qty)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Equipment */}
                {selectedRecipe.instructions && selectedRecipe.instructions.equipment && (
                  <div className='mt-8 order-4' ref={equipmentRef}>
                    <div className="flex items-center justify-between mb-5 pt-8 border-t-[1px] border-[var(--Color-Grays-Gray-3,#DCDCD8)]">
                      <h3 className="inline text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[18.8px] font-bold leading-[36px border-b-[2px] border-[#009900]">{tRecipes('drawer.equipment')}</h3>
                    </div>
                    <ul className="space-y-2">
                      {Array.isArray(selectedRecipe.instructions.equipment) ? (
                        selectedRecipe.instructions.equipment.map((item, idx) => (
                          <li key={idx} className="text-[var(--Color-Grays-Gray-950,#353227)] text-[14.8px] leading-[26px] flex items-start">
                            <span className="mr-3 text-[var(--Color-Grays-Gray-10,#0D0C07)]">•</span>
                            <span>{typeof item === 'string' ? item : item.equipment ? item.equipment : JSON.stringify(item)}</span>
                          </li>
                        ))
                      ) : selectedRecipe.instructions.equipments && Array.isArray(selectedRecipe.instructions.equipments) ? (
                        selectedRecipe.instructions.equipments.map((item, idx) => (
                          <li key={idx} className="text-[var(--Color-Grays-Gray-950,#353227)] text-[14.8px] leading-[26px] flex items-start">
                            <span className="mr-3 text-[var(--Color-Grays-Gray-10,#0D0C07)]">•</span>
                            <span>{typeof item === 'string' ? item : item.equipment ? item.equipment : JSON.stringify(item)}</span>
                          </li>
                        ))
                      ) : (
                        <li className="text-[var(--Color-Grays-Gray-950,#353227)] text-[14.8px] leading-[26px] flex items-start">
                          <span className="mr-3 text-[var(--Color-Grays-Gray-10,#0D0C07)]">•</span>
                          <span>{t('common:recipe.noEquipment', 'No equipment specified.')}</span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              
            </>
          )}
          {/* Footer with action buttons */}
          <div
          className="
            fixed bottom-[16px] left-[20px] right-[20px]
            py-1 px-4
            flex justify-center
            z-50
            mx-auto
            max-w-[390px]
            rounded-[96px]
            bg-[linear-gradient(135deg,_rgba(243,243,242,0.8)_0%,_rgba(243,243,242,0.9)_100%)]
            backdrop-blur-[64px]
            border-[1px] border-[#fff]
            shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
            transition-[bottom] duration-500 ease-in-out
          "
        >
            <div className="flex justify-between items-center gap-2 w-full">
              <button 
                onClick={() => scrollToSection(ingredientsRef)}
                className="
                  relative
                flex flex-col items-center gap-0 justify-center
                h-[48px] pt-[4px]
                rounded-[32px]
                w-[132px]
                text-[#0D0C07]
                font-normal
                text-[10.4px] font-normal leading-[16px]
                transition-colors
                md:hover:bg-[rgba(255,255,255,0.4)]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 25 24" fill="none">
                  <path d="M15.667 11L14.667 20M19.667 11L15.667 4M2.66699 11H22.667M4.16699 11L5.76699 18.4C5.86049 18.8586 6.11184 19.2698 6.47728 19.5621C6.84271 19.8545 7.2991 20.0094 7.76699 20H17.567C18.0349 20.0094 18.4913 19.8545 18.8567 19.5621C19.2221 19.2698 19.4735 18.8586 19.567 18.4L21.267 11M5.16699 15.5H20.167M5.66699 11L9.66699 4M9.66699 11L10.667 20" stroke="#65635D" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{tRecipes('drawer.ingredients')}</span>
              </button>
              <button 
                onClick={() => scrollToSection(instructionsRef)}
                className="
                    relative
                flex flex-col items-center gap-0 justify-center
                h-[48px] pt-[4px]
                rounded-[32px]
                w-[132px]
                text-[#0D0C07]
                font-normal
                text-[10.4px] font-normal leading-[16px]
                transition-colors
                md:hover:bg-[rgba(255,255,255,0.4)]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 25 24" fill="none">
                  <path d="M7.3335 21H17.3335M19.8335 12L22.3335 6M16.5835 3C16.8535 3.1 17.3835 3.53 17.3335 4.36C17.2735 5.19 16.4035 5.56 16.3335 6.38C16.2835 7.16 16.6735 7.62 17.0635 8M11.5835 3C11.8535 3.1 12.3835 3.53 12.3235 4.36C12.2735 5.19 11.3935 5.56 11.3435 6.38C11.2835 7.16 11.6735 7.62 12.0635 8M6.5835 3C6.8535 3.1 7.3835 3.53 7.3335 4.36C7.2735 5.19 6.4035 5.56 6.3335 6.38C6.2835 7.16 6.6735 7.62 7.0735 8M12.3335 21C14.7204 21 17.0096 20.0518 18.6975 18.364C20.3853 16.6761 21.3335 14.3869 21.3335 12H3.3335C3.3335 14.3869 4.28171 16.6761 5.96953 18.364C7.65736 20.0518 9.94655 21 12.3335 21Z" stroke="#65635D" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{tRecipes('drawer.instructions')}</span>
              </button>
              <button 
                onClick={() => scrollToSection(tipsRef)}
                className="
                    relative
                flex flex-col items-center gap-0 justify-center
                h-[48px] pt-[4px]
                rounded-[32px]
                w-[132px]
                text-[#0D0C07]
                font-normal
                text-[10.4px] font-normal leading-[16px]
                transition-colors
                md:hover:bg-[rgba(255,255,255,0.4)]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9H4.5C3.83696 9 3.20107 8.73661 2.73223 8.26777C2.26339 7.79893 2 7.16304 2 6.5C2 5.83696 2.26339 5.20107 2.73223 4.73223C3.20107 4.26339 3.83696 4 4.5 4H6M6 9V2H18V9M6 9C6 10.5913 6.63214 12.1174 7.75736 13.2426C8.88258 14.3679 10.4087 15 12 15C13.5913 15 15.1174 14.3679 16.2426 13.2426C17.3679 12.1174 18 10.5913 18 9M18 9H19.5C20.163 9 20.7989 8.73661 21.2678 8.26777C21.7366 7.79893 22 7.16304 22 6.5C22 5.83696 21.7366 5.20107 21.2678 4.73223C20.7989 4.26339 20.163 4 19.5 4H18M4 22H20M10 14.66V17C10 17.55 9.53 17.98 9.03 18.21C7.85 18.75 7 20.24 7 22M14 14.66V17C14 17.55 14.47 17.98 14.97 18.21C16.15 18.75 17 20.24 17 22" stroke="#65635D" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{tRecipes('drawer.tips')}</span>
              </button>
              <button 
                onClick={() => scrollToSection(equipmentRef)}
                className="
                        relative
                flex flex-col items-center gap-0 justify-center
                h-[48px] pt-[4px]
                rounded-[32px]
                w-[132px]
                text-[#0D0C07]
                font-normal
                text-[10.4px] font-normal leading-[16px]
                transition-colors
                md:hover:bg-[rgba(255,255,255,0.4)]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 25 24" fill="none">
                  <path d="M2.75 12H22.75M20.75 12V20C20.75 20.5305 20.5393 21.0392 20.1642 21.4143C19.7891 21.7893 19.2804 22 18.75 22H6.75C6.21957 22 5.71086 21.7893 5.33579 21.4143C4.96071 21.0392 4.75 20.5305 4.75 20V12M4.75 8.00004L20.75 4.00004M9.61 6.78004L9.16 4.97004C9.09553 4.71527 9.08189 4.4503 9.11988 4.19026C9.15787 3.93022 9.24674 3.68022 9.3814 3.45454C9.51606 3.22887 9.69388 3.03194 9.90469 2.87503C10.1155 2.71811 10.3552 2.60428 10.61 2.54004L12.55 2.06004C12.8054 1.99573 13.0711 1.98251 13.3316 2.02114C13.5922 2.05977 13.8426 2.14949 14.0684 2.28515C14.2941 2.42081 14.4909 2.59974 14.6474 2.81166C14.8038 3.02358 14.9169 3.26431 14.98 3.52004L15.43 5.32004" stroke="#65635D" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{tRecipes('drawer.equipment')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>, document.body
  );

  return (
    <>
      {portal}
      <ImagePreviewDrawer
        isOpen={!!previewImage}
        image={previewImage}
        closeDrawer={() => setPreviewImage(null)}
      />
    </>
  );
}

export default RecipeDrawer;
